import { useCallback, useEffect, useState } from "react";
import {
  ArrowRight,
  CalendarOff,
  Clock3,
  LoaderCircle,
  MapPinCheck,
  Plus,
  ShieldBan,
} from "lucide-react";

import { getCatalog, getWorkforce } from "./api";
import { getAvailability } from "./availability-api";
import { AvailabilityEditor, type AvailabilitySelection } from "./AvailabilityEditor";
import type {
  AvailabilityOverview,
  Catalog,
  ScopedAvailability,
  ServiceDurationRule,
  TravelBufferRule,
  WorkforceOverview,
} from "./types";

export type AvailabilitySection = keyof AvailabilityOverview;

const sectionCopy = {
  timeOff: {
    label: "Time off",
    singular: "time-off period",
    eyebrow: "People-first planning",
    title: "Protect approved time away.",
    copy: "Record team or employee leave as explicit scheduling exceptions, with exact dates and an audit trail.",
    icon: CalendarOff,
  },
  scheduleBlocks: {
    label: "Schedule blocks",
    singular: "schedule block",
    eyebrow: "Operational guardrails",
    title: "Reserve time before it becomes a conflict.",
    copy: "Block operational windows for a team or employee without inventing or confirming a customer appointment.",
    icon: ShieldBan,
  },
  durationRules: {
    label: "Duration rules",
    singular: "duration rule",
    eyebrow: "Configurable service logic",
    title: "Shape duration rules with intention.",
    copy: "Define effective-dated inputs for future scheduling while keeping unapproved commercial formulas inactive.",
    icon: Clock3,
  },
  travelBufferRules: {
    label: "Travel buffers",
    singular: "travel buffer",
    eyebrow: "Thoughtful routing",
    title: "Make space for realistic travel.",
    copy: "Configure global or region-to-region buffers ready for the conflict engine, without assuming final policy.",
    icon: MapPinCheck,
  },
} as const;

const emptyOverview: AvailabilityOverview = {
  timeOff: [],
  scheduleBlocks: [],
  durationRules: [],
  travelBufferRules: [],
};
const emptyWorkforce: WorkforceOverview = { regions: [], employees: [], teams: [] };
const emptyCatalog: Catalog = { services: [], addons: [] };

export function AvailabilityPage({ canWrite }: { canWrite: boolean }) {
  const [overview, setOverview] = useState(emptyOverview);
  const [workforce, setWorkforce] = useState(emptyWorkforce);
  const [catalog, setCatalog] = useState(emptyCatalog);
  const [section, setSection] = useState<AvailabilitySection>("timeOff");
  const [selection, setSelection] = useState<AvailabilitySelection>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [availability, organization, serviceCatalog] = await Promise.all([
        getAvailability(),
        getWorkforce(),
        getCatalog(),
      ]);
      setOverview(availability);
      setWorkforce(organization);
      setCatalog(serviceCatalog);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  const copy = sectionCopy[section];
  const items = overview[section];
  return (
    <div className="page-content availability-page">
      <header className="page-intro client-intro">
        <div>
          <span className="eyebrow lime">{copy.eyebrow}</span>
          <h1>{copy.title}</h1>
          <p>{copy.copy}</p>
        </div>
        {canWrite ? (
          <button
            className="primary-button compact-action"
            onClick={() => setSelection({ kind: section })}
          >
            <Plus /> Add {copy.singular}
          </button>
        ) : null}
      </header>

      <section className="availability-summary" aria-label="Availability configuration summary">
        <Summary label="Time off" value={overview.timeOff.length} icon={CalendarOff} />
        <Summary label="Blocks" value={overview.scheduleBlocks.length} icon={ShieldBan} />
        <Summary label="Duration rules" value={overview.durationRules.length} icon={Clock3} />
        <Summary
          label="Travel rules"
          value={overview.travelBufferRules.length}
          icon={MapPinCheck}
        />
      </section>

      <div className="availability-tabs" role="tablist" aria-label="Availability records">
        {(Object.keys(sectionCopy) as AvailabilitySection[]).map((key) => {
          const Icon = sectionCopy[key].icon;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={section === key}
              className={section === key ? "active" : ""}
              onClick={() => setSection(key)}
            >
              <Icon /> {sectionCopy[key].label}
            </button>
          );
        })}
      </div>

      {failed ? (
        <div className="load-error" role="alert">
          We couldn’t refresh availability configuration.
          <button onClick={() => void load()}>Try again</button>
        </div>
      ) : null}

      <section className="content-card availability-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Scheduling inputs</span>
            <h2>{copy.label}</h2>
          </div>
          <span className="catalog-count">{items.length}</span>
        </div>
        {loading && items.length === 0 ? (
          <div className="table-state">
            <LoaderCircle className="spin" /> Loading configuration…
          </div>
        ) : null}
        {!loading && items.length === 0 ? (
          <div className="workforce-empty">
            <strong>No {copy.label.toLowerCase()} configured.</strong>
            <span>Add a record only when the real operational rule is known.</span>
          </div>
        ) : null}
        <div className="availability-list">
          {section === "timeOff"
            ? overview.timeOff.map((item) => (
                <PeriodCard
                  key={item.id}
                  item={item}
                  icon={CalendarOff}
                  onOpen={() => setSelection({ kind: section, record: item })}
                />
              ))
            : null}
          {section === "scheduleBlocks"
            ? overview.scheduleBlocks.map((item) => (
                <PeriodCard
                  key={item.id}
                  item={item}
                  icon={ShieldBan}
                  onOpen={() => setSelection({ kind: section, record: item })}
                />
              ))
            : null}
          {section === "durationRules"
            ? overview.durationRules.map((item) => (
                <DurationCard
                  key={item.id}
                  item={item}
                  onOpen={() => setSelection({ kind: section, record: item })}
                />
              ))
            : null}
          {section === "travelBufferRules"
            ? overview.travelBufferRules.map((item) => (
                <TravelCard
                  key={item.id}
                  item={item}
                  onOpen={() => setSelection({ kind: section, record: item })}
                />
              ))
            : null}
        </div>
      </section>

      {selection ? (
        <AvailabilityEditor
          selection={selection}
          workforce={workforce}
          services={catalog.services}
          canWrite={canWrite}
          onClose={() => setSelection(undefined)}
          onSaved={async () => {
            setSelection(undefined);
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function Summary({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: typeof Clock3;
}) {
  return (
    <article>
      <span>
        <Icon /> {label}
      </span>
      <strong>{value}</strong>
    </article>
  );
}

function PeriodCard({
  item,
  icon: Icon,
  onOpen,
}: {
  item: ScopedAvailability;
  icon: typeof Clock3;
  onOpen: () => void;
}) {
  return (
    <button className="availability-row" onClick={onOpen}>
      <span className="availability-icon">
        <Icon />
      </span>
      <span className="availability-copy">
        <strong>{item.reason}</strong>
        <small>
          {item.employeeName ?? item.teamName} · {formatInstant(item.startAt)} –{" "}
          {formatInstant(item.endAt)}
        </small>
      </span>
      <Status active={item.active} />
      <ArrowRight />
    </button>
  );
}

function DurationCard({ item, onOpen }: { item: ServiceDurationRule; onOpen: () => void }) {
  return (
    <button className="availability-row" onClick={onOpen}>
      <span className="availability-icon">
        <Clock3 />
      </span>
      <span className="availability-copy">
        <strong>{item.name}</strong>
        <small>
          {item.serviceName} · {item.baseDurationMinutes} base minutes · Priority {item.priority}
        </small>
      </span>
      <Status active={item.active} />
      <ArrowRight />
    </button>
  );
}

function TravelCard({ item, onOpen }: { item: TravelBufferRule; onOpen: () => void }) {
  const route = `${item.fromRegionName ?? "Any region"} → ${item.toRegionName ?? "Any region"}`;
  return (
    <button className="availability-row" onClick={onOpen}>
      <span className="availability-icon">
        <MapPinCheck />
      </span>
      <span className="availability-copy">
        <strong>{item.name}</strong>
        <small>
          {route} · {item.bufferMinutes} minutes
        </small>
      </span>
      <Status active={item.active} />
      <ArrowRight />
    </button>
  );
}

function Status({ active }: { active: boolean }) {
  return (
    <span className={`availability ${active ? "active" : ""}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function formatInstant(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  }).format(new Date(value));
}
